import {useEmit} from '@malt/vue3-use';
import {computed, ref, ComputedRef, Ref} from 'vue';
import {Skill} from '@/common/types/Skill';
import {FreelancerSkillsService} from '@/common/api/FreelancerSkillsService';
import {HttpServiceResponse} from 'vue3-starter/src/typescript/http/http';
import {pushNotification} from '@malt/joy-entrypoint/src/js/notification';
import {useTranslation} from '@malt/vue3-use';

interface UseSkills {
    readonly freelancerSkillsService?: FreelancerSkillsService;
    readonly skills: Ref<Skill[]>;
    readonly minSkills: number;
    readonly maxSkills: number;
    readonly isThereTooMuchSkills: ComputedRef<boolean>;
    readonly isMaxSkills: ComputedRef<boolean>;
    readonly suggestedSkills: Ref<Skill[]>;
    readonly checkSuggestedSkills: () => void;
    readonly getSuggestedSkills: (category: string, jobTitle: string | null) => Promise<HttpServiceResponse<string[]>>;
    readonly valueChanged: (autoCompletedValue: string) => void;
    readonly onSkillAdded: (value: string) => void;
    readonly updateSkills: (newSkills: Skill[]) => void;
}

export function useSkills(initialSkills: Skill[] = [], freelancerSkillsService: FreelancerSkillsService | null): UseSkills {
    const emit = useEmit<{update: (value: Skill[]) => void; errorMax}>();
    const {msg} = useTranslation();

    const maxSkills = 6;
    const minSkills = 1;
    const suggestedSkills = ref<Skill[]>([]);
    const skills = ref<Skill[]>(initialSkills || []);
    const isThereTooMuchSkills = computed<boolean>(() => skills.value.length > maxSkills);
    const isMaxSkills = computed<boolean>(() => skills.value.length === maxSkills);

    const canAddMore = computed<boolean>(() => {
        return skills.value.length < maxSkills;
    });

    function checkSuggestedSkills(): void {
        suggestedSkills.value.forEach((skill) => {
            skill.disabled = !canAddMore || skills.value.filter((s) => s.name.toLowerCase() === skill.name.toLowerCase()).length > 0;
        });
    }

    async function getSuggestedSkills(category: string, jobTitle: string | null): Promise<HttpServiceResponse<string[]>> {
        const [data, error] = await freelancerSkillsService!.retrieveSuggestions(category, jobTitle);

        if (data && !error) {
            suggestedSkills.value = data.map((s) => new Skill(s));
            checkSuggestedSkills();
        }
        return [data, error];
    }

    function validateAutocompleteValue(autoCompletedValue: string) {
        const matchForbiddenCharacters = autoCompletedValue.match(/[,;]+/);
        return !matchForbiddenCharacters;
    }

    function valueChanged(autoCompletedValue: string): void {
        if (autoCompletedValue && autoCompletedValue.length > 0) {
            if (!validateAutocompleteValue(autoCompletedValue)) {
                return pushNotification(msg('project.proposal.skills.field.error.forbidden.characters'), {level: 'error'});
            }
            onSkillAdded(autoCompletedValue);
        }
    }

    function updateSkills(newSkills: Skill[]): void {
        skills.value = newSkills;
        checkSuggestedSkills();
        emit('update', skills.value);
    }

    function onSkillAdded(value: string): void {
        if (skills.value.length < maxSkills) {
            if (skills.value.filter((s) => s.name.toLowerCase() === value.toLowerCase()).length === 0) {
                skills.value.push(new Skill(value));
                emit('update', skills.value);
                checkSuggestedSkills();
            }
        } else {
            pushNotification(msg('project.proposal.skills.field.error.max.skills', maxSkills), {level: 'error'});
        }
    }

    return {
        skills,
        minSkills,
        maxSkills,
        suggestedSkills,
        isMaxSkills,
        isThereTooMuchSkills,
        checkSuggestedSkills,
        getSuggestedSkills,
        valueChanged,
        onSkillAdded,
        updateSkills,
    };
}
